// ============================================================================
// 자동화 규칙 관리 탭 (CHG-047: REQ-V47-013)
// ============================================================================

import { useState } from 'react';
import { Plus, FileCode, Trash2, Pencil, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAutomationRules, useToggleRule, useDeleteRule } from '../../hooks/useAutomation';
import type { ProjectDetail } from '../../services/projectService';
import RuleEditModal from './RuleEditModal';
import TemplateSelectModal from './TemplateSelectModal';
import AutomationLogView from './AutomationLogView';

interface Props {
  project: ProjectDetail;
  isProjectManager: boolean;
}

const EVENT_LABELS: Record<string, string> = {
  PR_OPENED: 'PR 생성',
  PR_REVIEW_REQUESTED: '리뷰 요청',
  PR_CHANGES_REQUESTED: '변경 요청',
  PR_APPROVED: '리뷰 승인',
  PR_MERGED: 'PR 머지',
  PR_CLOSED: 'PR 닫힘',
  BRANCH_CREATED: '브랜치 생성',
};

const LOG_STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  SUCCESS: { label: '성공', bg: 'bg-green-100', text: 'text-green-700' },
  PARTIAL: { label: '부분', bg: 'bg-amber-100', text: 'text-amber-700' },
  FAILED: { label: '실패', bg: 'bg-red-100', text: 'text-red-700' },
  SKIPPED: { label: '스킵', bg: 'bg-gray-100', text: 'text-gray-500' },
};

export default function AutomationRulesTab({ project, isProjectManager }: Props) {
  const { data: rules, isLoading } = useAutomationRules(project.id);
  const toggleMutation = useToggleRule(project.id);
  const deleteMutation = useDeleteRule(project.id);

  const [editingRule, setEditingRule] = useState<string | null>(null); // ruleId or 'new'
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [activeSection, setActiveSection] = useState<'rules' | 'logs'>('rules');

  const handleToggle = async (ruleId: string) => {
    try {
      await toggleMutation.mutateAsync(ruleId);
    } catch {
      toast.error('토글 실패');
    }
  };

  const handleDelete = async (ruleId: string, name: string) => {
    if (!confirm(`규칙 '${name}'을(를) 삭제하시겠습니까? 실행 로그도 함께 삭제됩니다.`)) return;
    try {
      await deleteMutation.mutateAsync(ruleId);
      toast.success('규칙이 삭제되었습니다.');
    } catch {
      toast.error('삭제 실패');
    }
  };

  return (
    <div className="space-y-6">
      {/* Section toggle */}
      <div className="flex items-center justify-between">
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button onClick={() => setActiveSection('rules')} className={`px-3 py-1.5 text-sm font-medium rounded-md ${activeSection === 'rules' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>
            규칙 ({rules?.length ?? 0})
          </button>
          <button onClick={() => setActiveSection('logs')} className={`px-3 py-1.5 text-sm font-medium rounded-md ${activeSection === 'logs' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>
            실행 로그
          </button>
        </div>
        {isProjectManager && activeSection === 'rules' && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowTemplateModal(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100">
              <FileCode className="size-4" /> 템플릿
            </button>
            <button onClick={() => setEditingRule('new')} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700">
              <Plus className="size-4" /> 규칙 추가
            </button>
          </div>
        )}
      </div>

      {/* Rules list */}
      {activeSection === 'rules' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="size-6 animate-spin text-gray-400" /></div>
          ) : !rules || rules.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400">
              자동화 규칙이 없습니다. 규칙을 추가하거나 템플릿을 적용하세요.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-sm font-medium text-gray-500">
                  <th className="text-left px-4 py-2">이름</th>
                  <th className="text-left px-4 py-2">트리거</th>
                  <th className="text-center px-4 py-2">조건</th>
                  <th className="text-left px-4 py-2">액션</th>
                  <th className="text-center px-4 py-2">활성</th>
                  <th className="text-center px-4 py-2">최근 실행</th>
                  {isProjectManager && <th className="text-center px-4 py-2 w-20">관리</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rules.map((rule) => {
                  const lastLog = rule.logs[0];
                  const logCfg = lastLog ? LOG_STATUS_CONFIG[lastLog.status] : null;
                  return (
                    <tr key={rule.ruleId} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <div className="text-sm font-medium text-gray-900">{rule.name}</div>
                        {rule.templateType && <span className="text-[11px] text-purple-500">템플릿</span>}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">{EVENT_LABELS[rule.triggerEvent] ?? rule.triggerEvent}</td>
                      <td className="px-4 py-2 text-center text-sm text-gray-500">{rule.conditions.length}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {rule.actions.map((a) => a.type === 'CHANGE_STATUS' ? '상태 변경' : a.type === 'ADD_COMMENT' ? '코멘트' : a.type === 'ASSIGN_USER' ? '담당자' : a.type).join(', ')}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => handleToggle(rule.ruleId)}
                          disabled={!isProjectManager || toggleMutation.isPending}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${rule.isEnabled ? 'bg-primary-600' : 'bg-gray-200'} ${!isProjectManager ? 'opacity-50' : 'cursor-pointer'}`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${rule.isEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                      </td>
                      <td className="px-4 py-2 text-center">
                        {logCfg ? (
                          <span className={`px-1.5 py-0.5 text-[11px] font-medium rounded ${logCfg.bg} ${logCfg.text}`}>{logCfg.label}</span>
                        ) : <span className="text-sm text-gray-400">—</span>}
                      </td>
                      {isProjectManager && (
                        <td className="px-4 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => setEditingRule(rule.ruleId)} className="p-1 text-gray-400 hover:text-blue-600"><Pencil className="size-3.5" /></button>
                            <button onClick={() => handleDelete(rule.ruleId, rule.name)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="size-3.5" /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Logs */}
      {activeSection === 'logs' && (
        <AutomationLogView projectId={project.id} rules={rules ?? []} />
      )}

      {/* Modals */}
      {editingRule && (
        <RuleEditModal
          projectId={project.id}
          ruleId={editingRule === 'new' ? null : editingRule}
          rules={rules ?? []}
          statuses={project.workflow.statuses}
          members={project.members}
          onClose={() => setEditingRule(null)}
        />
      )}
      {showTemplateModal && (
        <TemplateSelectModal
          projectId={project.id}
          statuses={project.workflow.statuses}
          onClose={() => setShowTemplateModal(false)}
        />
      )}
    </div>
  );
}
